import { MagicWandIcon, TimeOutlineIcon } from '@carboncopyco/icons';
import { GiveADemoIllustration } from '@carboncopyco/illustrations';
import type {
  DemoCreationData,
  IDemo,
  IVariable,
  Opportunity,
  Play,
  PlayAsset,
  TourCreationData,
} from '@carboncopyco/types';
import {
  Button,
  DropdownItem,
  InputTextField,
  Loading,
  LoadingDots,
  Modal,
  type ModalProps,
  SplitButtonDropdown,
  TextButton,
  TextCaption,
  TextSubHeader,
  Tooltip,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';

import {
  selectOrganizationId,
  useCreatePlayMutation,
  useGetIntegrationsConfigQuery,
  useLazyGetModificationsQuery,
  useLazyGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import { Debug, usePlayAnalytics } from '@carboncopyco/utils';
import {
  OpportunitiesDropdownField,
  OpportunitySelector,
  useIntegration,
} from '../../Integrations';
import { SalesforceNameLogo } from '../../Integrations/assets';
import { integrationVendorNameToIconMap } from '../../Integrations/consts';
import { useGiveADemo } from './GiveADemoContext';
import { playParentEntityToIdKey } from './consts';
import { getEntityType } from './utils';

const debug = Debug.extend('give-a-demo-modal');

type GiveADemoFormState = {
  forClient: string;
  opportunity?: Opportunity | null;
  variables: Record<IDemo['id'], IVariable[]>;
  speakerNotes: Record<IDemo['id'], string>;
};

type SubmitAction = 'save' | 'present';

const DONT_SHOW_PLAY_NOW = 'dontShowPlayNow';

const validationSchema = object({
  forClient: string().required('Required field'),
});

type OpportunityModalProps = {
  open: boolean;
  asset: PlayAsset;
  onClose: (redirectUrl?: string) => void;
  onPresent: (play: Play) => void;
  onCustomize: () => void;
  onSave: (play: Play) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  containerClass?: string;
  withNotificationOnPresent?: boolean;
  hidePlayLater?: boolean;
  workspaceId: string;
  hideCloseButton?: ModalProps['removeCloseIcon'];
};

export const OpportunityModal = ({
  open,
  asset,
  onClose,
  onPresent,
  onSave,
  onCustomize,
  onLoadingChange,
  containerClass,
  withNotificationOnPresent = false,
  hidePlayLater,
  workspaceId,
  hideCloseButton,
}: OpportunityModalProps) => {
  const flags = useFlags();
  const {
    trackPlayOpenCustomizeModal,
    trackPlayClickPlayNow,
    trackPlayClickPlayLater,
  } = usePlayAnalytics('give_a_demo_modal', {
    [playParentEntityToIdKey[asset.type]]: asset.id,
  });
  const [getPresentationsByDemo] = useLazyGetPresentationsByDemoQuery();
  const { submissionValues, setSubmissionValues } = useGiveADemo();
  const organizationIdFromStore = useSelector(selectOrganizationId);
  const { organizationId: organizationIdFromParams } = useParams();
  const organizationId = organizationIdFromStore || organizationIdFromParams;
  const inputRef = useRef<HTMLInputElement>(null);
  const opportunitiesInputRef = useRef<HTMLInputElement>(null);
  const [submitAction, setSubmitAction] = useState<SubmitAction>();
  const [getModifications] = useLazyGetModificationsQuery();
  const [initialValues, setInitialValues] = useState<GiveADemoFormState>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const sfdcRef = useRef<HTMLDivElement>(null);
  const playNowRef = useRef<HTMLDivElement>(null);

  const getIsPlayTooltipShown = (): string => {
    return localStorage.getItem(DONT_SHOW_PLAY_NOW);
  };

  const handleInitialValues = async () => {
    const assetsVariablesMap = asset.assets.map(
      async ({ entity, assetRevisionId }) => {
        const isExternalUrlType = getEntityType(entity) === 'externalUrl';
        const isTourType = getEntityType(entity) === 'tour';

        if (isExternalUrlType || isTourType) {
          return {
            id: entity.id,
            variables: [],
          };
        }

        const variables = await getModifications({
          organizationId,
          entityType: 'demo',
          modificationType: 'variable',
          revisionId: String(assetRevisionId),
          entityId: String(entity.id ?? ''),
          apiVersion: 2,
        }).unwrap();

        const variablesContent = variables.map((variable) => variable.content);

        return {
          id: entity.id,
          variables: variablesContent,
        };
      }
    );

    const promisedVariables = await Promise.all(assetsVariablesMap);

    const values = {
      forClient: submissionValues.forClient ?? '',
      opportunity: submissionValues.opportunity ?? null,
      variables: promisedVariables.reduce((acc, { id, variables }) => {
        return {
          ...acc,
          [id]: variables,
        };
      }, {}),
      speakerNotes: submissionValues.speakerNotes,
    };

    setInitialValues(values);
    setSubmissionValues(values);
  };
  const [createPlay, { isLoading }] = useCreatePlayMutation();

  const {
    paused: isSalesforcePaused,
    integrationId: salesforceIntegrationId,
    renewalRequired: salesforceRenewalRequired,
    activeOpportunities: salesforceActiveOpportunities,
  } = useIntegration(
    {
      integrationType: 'salesforce',
      organizationId,
    },
    { skip: flags.hubspotCrm }
  );

  const { data: config } = useGetIntegrationsConfigQuery(
    {
      organizationId: organizationIdFromParams,
    },
    { skip: !flags.hubspotCrm }
  );
  const integrationType = config?.crm;
  const { paused, integrationId, renewalRequired } = useIntegration(
    {
      integrationType,
      organizationId,
    },
    { skip: !flags.hubspotCrm }
  );
  const withIntegration = flags.hubspotCrm
    ? !!integrationId && !paused
    : !!salesforceIntegrationId && !isSalesforcePaused;

  const handleCreatePresentation = async (
    modalValues: GiveADemoFormState,
    onCreateSuccess?: (play: Play) => void
  ) => {
    const { forClient } = modalValues;
    const { variables } = submissionValues;
    const createPresentationAssets = asset.assets.map(
      ({ assetRevisionId, entity }) => {
        const getVariables = () => (entity && variables[entity.id]) ?? [];

        const isTourType = getEntityType(entity) === 'tour';

        return {
          assetId: entity.id,
          assetType: getEntityType(entity),
          assetRevisionId: Number(assetRevisionId),
          creationData: {
            forClient,
            notes: submissionValues.speakerNotes[entity.id] ?? '',
            ...(isTourType
              ? ({
                  tourId: entity.id,
                } as Pick<TourCreationData, 'tourId'>)
              : ({
                  demoId: entity.id,
                  variables: getVariables(),
                } as Pick<DemoCreationData, 'demoId' | 'variables'>)),
          },
        };
      }
    );
    createPlay({
      organizationId,
      workspaceId,
      assetId: Number(asset.id),
      assetType: asset.type,
      settings: {
        forClient,
        integrationMetadata:
          modalValues.opportunity?.id &&
          (flags.hubspotCrm ? integrationId : salesforceIntegrationId)
            ? {
                integrationId: flags.hubspotCrm
                  ? integrationId
                  : salesforceIntegrationId,
                opportunityId: modalValues.opportunity.id,
              }
            : undefined,
        playLater: submitAction === 'save' || undefined,
      },
      assets: createPresentationAssets,
    })
      .unwrap()
      .then((response) => {
        if (response.asset.assetType === 'demo') {
          getPresentationsByDemo({
            workspaceId,
            organizationId,
            demoId: String(response.asset.assetId),
            clearCache: true,
          });
        }
        onCreateSuccess?.(response);
        if (withNotificationOnPresent) {
          notify.success(
            `${modalValues.forClient} presentation was saved successfully!`
          );
        }
      })
      .catch((error) => {
        notify.error(
          `${modalValues.forClient} presentation was not saved, please try again`
        );
        debug(error);
      })
      .finally(onClose);
  };

  const handleSubmit = async (values: GiveADemoFormState) => {
    if (submitAction === 'present') {
      await handleCreatePresentation(values, onPresent);
    }
    if (submitAction === 'save') {
      await handleCreatePresentation(values, onSave);
    }
  };

  useEffect(() => {
    handleInitialValues();
  }, []);

  useEffect(() => {
    if (!open || !initialValues) {
      return;
    }
    if (withIntegration) {
      opportunitiesInputRef.current?.focus();
      return;
    }
    inputRef.current?.focus();
  }, [initialValues]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [onLoadingChange, isLoading]);

  const handleCloseModal = () => {
    setIsModalOpened(false);
    const giveADemoParam = searchParams.get('give-a-demo');
    if (giveADemoParam) {
      searchParams.delete('give-a-demo');
    }
    setSearchParams();
    onClose();
  };

  useEffect(() => {
    const giveADemoParam = searchParams.get('modal');
    const isGiveADemoParam = giveADemoParam === 'give-a-demo';
    if (isGiveADemoParam) {
      setIsModalOpened(true);
    }
  }, []);

  const handleSubmitAction = (action: SubmitAction) => {
    setSubmitAction(action);
    if (!getIsPlayTooltipShown()) {
      localStorage.setItem(DONT_SHOW_PLAY_NOW, 'true');
    }
  };

  const IntegrationLogo = useMemo(
    () => integrationVendorNameToIconMap[integrationType],
    [integrationType]
  );

  return (
    <Modal
      open={open || isModalOpened}
      noPadding
      floatingCloseButton
      onClose={handleCloseModal}
      closeOnlyFromCloseIcon={!hideCloseButton}
      locked={hideCloseButton}
      containerClass={containerClass}
      removeCloseIcon={hideCloseButton}
    >
      <div className="flex flex-col w-ds-md-modal">
        <div className="flex justify-center bg-ds-natural-50 h-[246px] pb-2 items-end rounded-t-md">
          <GiveADemoIllustration />
        </div>

        <div className="p-6">
          {initialValues && (
            <TooltipBubble
              placement="top-start"
              title={asset.name ?? 'demo'}
            >
              <div className="pt-2 pb-6">
                <TextSubHeader
                  label={`Play "${asset.name ?? 'demo'}"`}
                  color="text-ds-black"
                  ellipsis
                />
              </div>
            </TooltipBubble>
          )}
          <div className="h-36">
            {initialValues ? (
              <Formik<GiveADemoFormState>
                initialErrors={{
                  opportunity: undefined,
                  forClient: undefined,
                }}
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleSubmit: submit, isValid }) => {
                  return (
                    <Form>
                      <div className="flex flex-col flex-grow ds-input-text-field-wrapper">
                        <div className="relative">
                          {withIntegration ? (
                            <div className="mb-4 w-full">
                              {flags.hubspotCrm ? (
                                <OpportunitySelector
                                  ref={opportunitiesInputRef}
                                  clientFieldName="forClient"
                                  isIntegrationConnected={!renewalRequired}
                                  integrationLogo={<IntegrationLogo />}
                                />
                              ) : (
                                <OpportunitiesDropdownField
                                  ref={opportunitiesInputRef}
                                  clientFieldName="forClient"
                                  integrationName="Salesforce"
                                  opportunities={
                                    salesforceActiveOpportunities || []
                                  }
                                  opportunityListTitle="salesforce results"
                                  isIntegrationConnected={
                                    !salesforceRenewalRequired
                                  }
                                  integrationLogo={<SalesforceNameLogo />}
                                />
                              )}
                            </div>
                          ) : (
                            <InputTextField
                              ref={inputRef}
                              name="forClient"
                              label="Opportunity"
                              placeholder="Opportunity name"
                              clearOnFocus
                              inputSize="lg"
                            />
                          )}
                          {!flags.hubspotCrm && !salesforceIntegrationId && (
                            <>
                              <div
                                ref={sfdcRef}
                                className="absolute top-0 -mt-2 right-0 pb-1"
                              >
                                <div className="flex flex-row align-center justify-center">
                                  <MagicWandIcon
                                    color="#0078EF"
                                    size={16}
                                  />
                                  <div className="pl-2">
                                    <TextButton
                                      label="Tip"
                                      color="text-ds-primary-300"
                                    />
                                  </div>
                                </div>
                              </div>
                              <Tooltip
                                anchorRef={sfdcRef}
                                placement="top-start"
                                arrowDown
                                arrowPosition="start"
                              >
                                <div className="p-4 w-80">
                                  <div>
                                    <TextButton
                                      label="Demostack with SFDC"
                                      color="text-ds-natural-600"
                                    />
                                  </div>

                                  <TextCaption
                                    label="Get a complete view of your demo data by syncing Demostack with SFDC."
                                    color="text-ds-natural-600"
                                  />
                                </div>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end mt-5 gap-4.5">
                        {isLoading && <Loading color="text-ds-primary-300" />}
                        <div className="flex flex-row-reverse gap-4.5">
                          {!getIsPlayTooltipShown() && (
                            <Tooltip
                              anchorRef={playNowRef}
                              placement="top-start"
                              arrowDown
                              arrowPosition="start"
                            >
                              <div className="p-4 w-80">
                                <div className="flex">
                                  <MagicWandIcon color="#0078EF" />
                                  <div className="pl-1">
                                    <TextButton
                                      label={
                                        hidePlayLater
                                          ? 'Play now!'
                                          : 'Play now, Or Play later!'
                                      }
                                      color="text-ds-natural-600"
                                    />
                                  </div>
                                </div>
                                {!hidePlayLater && (
                                  <TextCaption
                                    label="You can customize your presentation and save it for later by clicking on the dropdown bellow"
                                    color="text-ds-natural-600"
                                  />
                                )}
                              </div>
                            </Tooltip>
                          )}
                          <div ref={playNowRef}>
                            {hidePlayLater ? (
                              <Button
                                label="Play now"
                                onClick={() => {
                                  trackPlayClickPlayNow();
                                  handleSubmitAction('present');
                                  submit();
                                }}
                                disabled={!isValid || isLoading}
                              />
                            ) : (
                              <SplitButtonDropdown
                                label="Play now"
                                onClick={() => {
                                  trackPlayClickPlayNow();
                                  handleSubmitAction('present');
                                  submit();
                                }}
                                disabled={!isValid || isLoading}
                                dropdownProps={{
                                  width: 216,
                                  align: 'end',
                                }}
                              >
                                <DropdownItem
                                  iconLeft={<TimeOutlineIcon size={20} />}
                                  label="Play later"
                                  onClick={() => {
                                    trackPlayClickPlayLater();
                                    handleSubmitAction('save');
                                    submit();
                                  }}
                                />
                              </SplitButtonDropdown>
                            )}
                          </div>
                          <Button
                            link
                            blue
                            label="Customize"
                            onClick={() => {
                              trackPlayOpenCustomizeModal();
                              setSubmissionValues({
                                ...values,
                                variables: submissionValues.variables,
                                speakerNotes: submissionValues.speakerNotes,
                              });
                              onCustomize();
                            }}
                          />
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            ) : (
              <div className="flex justify-center relative">
                <LoadingDots />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
